// WeatherWidget.jsx – daily weather for the farms' center (Open‑Meteo API)
import React, { useState, useEffect } from "react";
import ArrowIcon from "../assets/icons/arrow-icon.svg";

// WMO weather code → Persian description (subset)
const weatherDescriptions = {
  0: "صاف",
  1: "عمدتاً صاف",
  2: "نیمه ابری",
  3: "ابری",
  45: "مه",
  48: "مه نشسته",
  51: "باران سبک",
  53: "باران ملایم",
  55: "باران شدید",
  61: "رگبار خفیف",
  63: "رگبار متوسط",
  65: "رگبار شدید",
  71: "برف خفیف",
  73: "برف متوسط",
  75: "برف سنگین",
  80: "رگبار پراکنده",
  81: "رگبار متوسط",
  82: "رگبار شدید",
  95: "طوفان تندری",
  96: "طوفان تندری با تگرگ",
  99: "طوفان تندری شدید با تگرگ",
};

// Emojis for weather icons (replaces SVGs)
const weatherIcons = {
  0: "☀️",
  1: "🌤️",
  2: "⛅",
  3: "☁️",
  45: "🌫️",
  48: "🌫️",
  51: "🌦️",
  53: "🌦️",
  55: "🌧️",
  61: "🌧️",
  63: "🌧️",
  65: "🌧️",
  71: "🌨️",
  73: "🌨️",
  75: "❄️",
  80: "🌦️",
  81: "🌧️",
  82: "🌧️",
  95: "⛈️",
  96: "⛈️",
  99: "⛈️",
};

const WeatherWidget = ({ center }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(2); // today = index 2

  // Format date for Persian display
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("fa-IR", { month: "short", day: "numeric" });
  };

  const getCoordinates = (value) => {
    if (!value || typeof value !== "object") return null;

    const lat = Number(value.lat ?? value.latitude);
    const lon = Number(value.lon ?? value.lng ?? value.longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;

    return { lat, lon };
  };

  // Fetch daily weather for 5‑day window around today
  useEffect(() => {
    const coords = getCoordinates(center);

    if (!coords) {
      setWeatherData(null);
      setError(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchWeather = async () => {
      setLoading(true);
      setError(false);
      try {
        const url = new URL("https://api.open-meteo.com/v1/forecast");
        url.searchParams.set("latitude", String(coords.lat));
        url.searchParams.set("longitude", String(coords.lon));
        url.searchParams.set("daily", "weather_code,temperature_2m_max,temperature_2m_min");
        url.searchParams.set("timezone", "auto");
        url.searchParams.set("past_days", 2);
        url.searchParams.set("forecast_days", 3);

        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error("API request failed");
        const json = await response.json();
        const daily = json.daily;

        if (!daily?.time || !daily?.weather_code || !daily?.temperature_2m_max || !daily?.temperature_2m_min) {
          throw new Error("Unexpected weather payload");
        }

        const days = daily.time.map((date, i) => ({
          date,
          weather_code: daily.weather_code[i],
          temp_max: daily.temperature_2m_max[i],
          temp_min: daily.temperature_2m_min[i],
        }));

        setWeatherData(days);
        setCurrentIndex(2);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Weather fetch error:", err);
        setError(true);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchWeather();

    return () => controller.abort();
  }, [center]);

  const handlePrev = () => {
    if (!weatherData) return;
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    if (!weatherData) return;
    setCurrentIndex((prev) => Math.min(weatherData.length - 1, prev + 1));
  };

  const currentDay = weatherData ? weatherData[currentIndex] : null;
  const description = currentDay
    ? weatherDescriptions[currentDay.weather_code] || "نامشخص"
    : "";
  const icon = currentDay ? weatherIcons[currentDay.weather_code] || "❓" : "";

  // Show a simple message (same height as the real widget) when there is no data yet
  if (!center) {
    return (
      <div className="weather-date-navigation weather-placeholder">
        <div className="weather-center">اطلاعات آب‌وهوا در دسترس نیست</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="weather-date-navigation weather-placeholder">
        <div className="weather-center">در حال بارگذاری...</div>
      </div>
    );
  }

  if (error || !weatherData) {
    return (
      <div className="weather-date-navigation weather-placeholder">
        <div className="weather-center">داده‌ها در دسترس نیستند</div>
      </div>
    );
  }

  // Full weather widget with arrows and live data
  return (
    <div className="weather-date-navigation">
      <button
        className="weather-nav-btn prev-btn"
        onClick={handlePrev}
        disabled={currentIndex === 0}
      >
        <img src={ArrowIcon} className="nav-arrow-icon" alt="Prev" />
      </button>

      <div className="weather-center">
        <div className="weather-text">
          <div className="weather-desc">{description}</div>
          <div className="weather-date">{formatDate(currentDay.date)}</div>
        </div>
        <div className="weather-temp">{Math.round(currentDay.temp_max)}°</div>
        <div className="weather-icon">{icon}</div>
      </div>

      <button
        className="weather-nav-btn next-btn"
        onClick={handleNext}
        disabled={currentIndex === weatherData.length - 1}
      >
        <img src={ArrowIcon} className="nav-arrow-icon" alt="Next" />
      </button>
    </div>
  );
};

export default WeatherWidget;